<template>
    <v-container>
        <form-toolbar :canEdit="canEdit" :userRoles="userRoles" :editMode="editMode"  :field="'construction'" :assignment="assignment" @cancel-button-clicked="switchEditMode(false)" @edit-button-clicked="switchEditMode(true)"/>
        <autopsy-info  :assignment="this.assignment" :propertyTab="'construction'"/>
        <v-form :readonly="readOnly" class="assignment-form">
            <v-card class = "form-group-fields" elevation="6">
                <div class="first-row">
                    <custom-datetime :value="localConstruction.appointmentDate || ''" :readOnly="true" @onSelect="appointmentDateSelected"/>
                    <v-select
                        v-model="localConstruction.status"
                        :items="statuses"
                        item-value="value"
                        item-text="name"
                        label="Κατάσταση"
                        outlined
                        class="field status"
                    ></v-select>
                    <custom-checkbox
                        v-if="assignment.type === 'PILOT'"
                        :readOnly="readOnly"
                        :label="'Smart Readiness'"
                        :color="'blue'"
                        change-back
                        :value="!!this.smartReadiness"
                        class="m-2"
                        @check-value-change="onSmartReadinessChange"
                    />
                    <v-dialog
                        v-if="this.assignment?.autopsy?.floorStructure"
                        v-model="dialog"
                        width="500"
                    >
                        <template v-slot:activator="{ on, attrs }">
                            <v-btn
                                v-bind="attrs"
                                v-on="on"
                                class="white--text button-to-display"
                                large
                                :color="'black'"
                                :depressed="editMode">
                                <span>Δομη Κτιριου</span>
                            </v-btn>
                        </template>
                        <floor-structure :editMode="false" :stored="this.assignment.autopsy.floorStructure" />
                    </v-dialog>
                </div>
                <comments
                    ref="comments"
                    :comments="this.localConstruction?.comments || {}"
                    :clearable="clearable"
                    :editMode="editMode"
                    :rules="editMode ? rules: []"
                    @new-comment="onNewComment"
                />
                <material-selection v-if="config.hasStorage" ref="material-selection" :editMode="editMode" :assignmentTab="'construction'" :assignment="assignment" />
                <image-section ref="image-section" :uploadedPhotos="this.localConstruction.photos || []" :editMode="editMode" :readOnly="readOnly" :clearable="clearable" :uploadPath="this.assignment.sr + '/construction'" @photoSelection="onPhotoSelection" :zipName="this.assignment.sr + '_' + 'construction'"/>
                <v-divider class="mt-5"/>
                <issue-existance-field
                    ref="prodesmeusi-difference-section"
                    :label="'Διαφορά από Προδέσμευση'"
                    :check="hasProdesmeusiDifference"
                    :comments="localConstruction.prodesmeusiDifference?.comments || {}"
                    :readOnly="readOnly"
                    :clearable="clearable"
                    :editMode="editMode"
                    noImages
                />
                <v-divider />
                <issue-existance-field
                    ref="malfunction-section"
                    :label="'Βλάβη / Πρόβλημα'"
                    :photoLabel="'Φωτογραφίες Βλάβης'"
                    :check="hasMalfunction"
                    :comments="localConstruction.malfunction?.comments || {}"
                    :uploadedPhotos="localConstruction.malfunction?.photos || []"
                    :readOnly="readOnly"
                    :clearable="clearable"
                    :editMode="editMode"
                    :uploadPath="this.assignment.sr + '/construction/malfunction'"
                    @onIssuePhotoSelection="onTroublePhotoSelection"
                />
                <v-divider />
                <div class = "last-row">
                    <v-checkbox
                        v-model="localConstruction.otoCompleted"
                        label="Πραγματοποίηση ΟΤΟ"
                        color="green"
                        :value="true"
                        class="field completed-checkbox"
                    ></v-checkbox>
                    <v-textarea
                        v-if="localConstruction.otoCompleted"
                        v-model="localConstruction.otoDescription"
                        filled
                        label="Περιγραφή ΟΤΟ"
                        rows="10"
                        row-height="30"
                        :clearable="clearable"
                        :rules="editMode && localConstruction.otoCompleted ? rules: []"
                        class="field"
                    ></v-textarea>
                </div>
            </v-card>
            <div v-if="canEdit" class="submit-button-wrapper">
                <v-btn class="mx-2 white--text" :disabled="shouldDisableSubmit" :loading="loading" large color="black" @click="onSubmit">
                    ΥΠΟΒΟΛΗ
                </v-btn>
            </div>
            <waiting-message v-if="waitingMessage" :waiting-message="waitingMessage" />
        </v-form>
        <alert :show-alert="showAlert" :message="message" :type="type" />
    </v-container>
</template>

<script>
    import assignmentService from '@/services/assignment'
    import Alert from '@/components/Alert'
    import ImageSection from '@/components/ImageSection'
    import MaterialSelection from '@/components/MaterialSelection'
    import FormToolbar from '@/components/FormToolbar'
    import geolocation from '@/utils/geolocation'
    import FloorStructure from '@/components/FloorStructure'
    import Comments from '@/components/Comments'
    import momentTimezone from 'moment-timezone'
    import CustomDatetime from '@/components/CustomDatetime'
    import CustomCheckbox from '@/components/CustomCheckBox'
    import IssueExistanceField from '@/components/IssueExistanceField'
    import AutopsyInfo from '@/components/AutopsyInfo'
    import WaitingMessage from '@/components/WaitingMessage'

    export default {
        name: 'ConstructionTab',
        components: { Alert, AutopsyInfo, Comments, CustomDatetime, IssueExistanceField, CustomCheckbox, ImageSection, FormToolbar, MaterialSelection, FloorStructure, WaitingMessage },
        props: {
            assignment: {
                type: Object,
                required: true
            },
            currentUser: {
                type: Object,
                default: () => { return {} },
                required: true
            },
            canEdit: {
                type: Boolean,
                required: true
            }
        },
        watch: {
            assignment: {
                handler(val, oldVal) {
                    this.resetForm()
                },
                deep: true
            }
        },
        computed: {
            userRoles() {
                return this.currentUser?.roles?.map(role => role.role) || []
            },
            shouldDisableSubmit() {
                if (this.localConstruction.status === 'FOR_CANCELLING') {
                    return false
                }
                return !(this.localConstruction.appointmentDate && (this.localConstruction.comments || this.newComment) && (!this.localConstruction.otoCompleted || (this.localConstruction.otoCompleted ? this.localConstruction.otoDescription : false)) && this.editMode)
            },
            config() {
                return this.$store.getters.getConfig || {}
            },
            statuses() {
                return this.config?.construction?.statuses || []
            }
        },
        data: () => {
            return {
                localConstruction: {
                    description: null,
                    photos: [],
                    troubleshootingPhotos: []
                },
                hasMalfunction: false,
                hasProdesmeusiDifference: false,
                newComment: null,
                selectedPhotos: 0,
                smartReadiness: false,
                showAlert: false,
                message: null,
                dialog: false,
                type: null,
                loading: false,
                mode: null,
                readOnly: false,
                clearable: true,
                selectedTroublePhotos: 0,
                rules: [
                    v => !!v || 'Το πεδίο είναι υποχρεωτικό'
                ],
                editMode: false,
                waitingMessage: null
            }
        },
        created() {
            this.mode = this.$route.params.mode
            if (this.mode === 'edit') {
                this.readOnly = true
                this.clearable = false
            }
            this.assignmentId = this.$route.params.assignmentId
        },
        mounted() {
            this.resetForm()
        },
        methods: {
            onSmartReadinessChange(e) {
                this.smartReadiness = e
            },
            appointmentDateSelected(datetime) {
                this.$set(this.localConstruction, 'appointmentDate', momentTimezone(datetime).tz('Europe/Athens').utc().format('YYYY-MM-DD[T]HH:mm:ss[Z]').toString())
            },
            onNewComment(e) {
                this.newComment = e
            },
            onTroublePhotoSelection(noOfSelectedPhotos) {
                this.selectedTroublePhotos = noOfSelectedPhotos
            },
            tabSelect(tab) {
                this.selectedTab = tab
            },
            switchEditMode(editMode) {
                if (!this.canEdit) {
                    return
                }
                this.resetForm()
                if (editMode) {
                    this.readOnly = false
                    this.clearable = true
                    this.editMode = true
                } else {
                    this.readOnly = true
                    this.clearable = false
                    this.editMode = false
                }
            },
            onUploadPhotoError(message) {
                this.setAlert(message, 'error')
            },
            onPhotoSelection(selectedPhotos) {
                this.selectedPhotos = selectedPhotos.length
            },
            resetForm() {
                this.localConstruction = this.assignment.construction ? JSON.parse(JSON.stringify(this.assignment.construction)) : {}
                this.hasMalfunction = !!this.localConstruction.malfunction
                this.hasProdesmeusiDifference = !!this.localConstruction.prodesmeusiDifference
                this.smartReadiness = this.assignment.smartReadiness
            },
            setAlert(message, type) {
                this.showAlert = true
                this.message = message
                this.type = type
                setTimeout(() => { this.showAlert = false }, 7000)
            },
            async editAssignment() {
                this.loading = true
                const coords = await geolocation.getCoordinates()
                if (!this.localConstruction.photos) {
                    this.localConstruction.photos = []
                }

                const malfunctionSection = this.$refs['malfunction-section'].getIssueValues()
                this.hasMalfunction = malfunctionSection.checked
                if (this.hasMalfunction) {
                    this.localConstruction.malfunction = this.localConstruction.malfunction || { photos: [] }
                    this.localConstruction.malfunction.newComment = malfunctionSection.newComment
                }
                this.waitingMessage = 'Ανέβασμα Αρχείων...'
                if (this.isAdminOrCompanyCoordinator) {
                    try {
                        const deletedPhotos = await this.$refs['image-section'].deleteUploadedPhotos()
                        this.localConstruction.photos = deletedPhotos ? this.localConstruction.photos.filter(photo => !deletedPhotos.includes(photo)) : [ ...this.localConstruction.photos ]
                        if (this.hasMalfunction) {
                            const deletedTroubleshootingPhotos = await this.$refs['malfunction-section'].deleteUploadedPhotos()
                            this.localConstruction.malfunction.photos = deletedTroubleshootingPhotos ? this.localConstruction.malfunction.photos.filter(photo => !deletedTroubleshootingPhotos.includes(photo)) : [ ...this.localConstruction.malfunction.photos ]
                        }
                    } catch (error) {
                        this.setAlert(error.message, 'error')
                        this.loading = false
                        this.waitingMessage = null
                        return
                    }
                }

                try {
                    const uploadedPhotos = await this.$refs['image-section'].uploadPhotos()
                    this.localConstruction.photos = uploadedPhotos ? this.localConstruction.photos.concat(uploadedPhotos) : [ ...this.localConstruction.photos ]
                    if (this.hasMalfunction) {
                        const uploadedTroubleshootingPhotos = await this.$refs['malfunction-section'].uploadPhotos()
                        this.localConstruction.malfunction.photos = uploadedTroubleshootingPhotos ? this.localConstruction.malfunction.photos.concat(uploadedTroubleshootingPhotos) : [ ...this.localConstruction.malfunction.photos ]
                    }
                } catch (error) {
                    this.setAlert(error.message, 'error')
                    this.loading = false
                    this.waitingMessage = null
                    return
                }

                const prodesmeusiDifferenceSection = this.$refs['prodesmeusi-difference-section'].getIssueValues()
                this.hasProdesmeusiDifference = prodesmeusiDifferenceSection.checked
                if (this.hasProdesmeusiDifference) {
                    this.localConstruction.prodesmeusiDifference = this.localConstruction.prodesmeusiDifference || {}
                    this.localConstruction.prodesmeusiDifference.newComment = prodesmeusiDifferenceSection.newComment
                }
                this.localConstruction.geolocation = coords
                this.localConstruction.newComment = this.newComment
                this.localConstruction.smartReadiness = this.smartReadiness
                if (this.config.hasStorage) {
                    this.localConstruction.materialsRequested = this.$refs['material-selection'].proccessMaterialRequested()
                }
                assignmentService.updateAssignmentTab('construction', this.assignment.id, { construction: this.localConstruction }).then((response) => {
                    this.$refs['comments'].clear()
                    this.$refs['malfunction-section'].clear()
                    this.$refs['prodesmeusi-difference-section'].clear()
                    this.selectedMaterials = []
                    this.switchEditMode(false)
                    this.setAlert('Η ενημέρωση της Κατασκευής ήταν επιτυχημένη', 'success')
                    this.$emit('assignment-refresh')
                }).catch((error) => {
                    this.setAlert(error?.response?.data?.message || 'Προέκυψε κάποιο σφάλμα. Παρακαλώ επικοινωνήστε με τον Διαχειριστή', 'error')
                }).finally(() => {
                    this.waitingMessage = null
                    this.loading = false
                })
            },
            async onSubmit() {
                if (this.mode === 'edit') {
                    this.editAssignment()
                }
            }
        }

    }
</script>
<style scoped>
.button-to-display {
    margin-left: 1rem;
    margin-right: 1rem;
}
.first-row {
    display: flex;
    justify-content: space-evenly;
    flex-wrap: wrap;
    align-items: first baseline;
    margin-top: 0.8rem;
}

.last-row {
    display: flex;
    padding: 0.3rem;
    flex-direction: column;
}
.form-group-fields {
    width: 100%;
    display: flex;
    flex-direction: column;
    flex-wrap: wrap;
}

.body-label {
    color: rgba(0, 0, 0, 0.6);
}

.body-editor {
    margin-bottom: 2vh;
}

.edit-button-wrapper {
    display: flex;
    justify-content: flex-start;
}

.submit-button-wrapper {
    width: 100%;
    display: flex;
    justify-content: center;
    margin-top: 2vh;
}

.field.completed-checkbox {
    font-weight: 800;
}

.field.status {
    min-width: 270px;
    max-width: 20%;
}

.field.completed-checkbox {
    font-weight: 800;
}

.v-card {
    padding: 0.8rem;
    color: var(--cards-and-tables-text-color) !important;
    background: var(--cards-and-tables-color) !important;
}

</style>
